import React, { useEffect, useState } from 'react';
import {
  Button, Modal, Form, Input, Select, Space, message, Empty, Popconfirm, Grid,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fiscalDataService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { useBreakpoint } = Grid;

const USOS = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'S01', label: 'S01 - Sin efectos fiscales' },
  { value: 'CP01', label: 'CP01 - Pagos' },
];

/**
 * Clientes (receptores) del contribuyente, mantenidos por el gestor.
 *
 * Existe porque las cuentas gestionadas son VISORES: el contribuyente no puede
 * mantener sus propios receptores, así que si no lo hace el gestor no lo hace
 * nadie. Escribe por la ruta delegada, que resuelve el alcance y deja bitácora.
 */
const ClientesPanel = ({ empresa }) => {
  const screens = useBreakpoint();
  const esMovil = !screens.md;
  const companyId = empresa?.id;

  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null); // null = cerrado, {} = alta
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();

  const cargar = async () => {
    if (!companyId) return;
    setCargando(true);
    try {
      const data = await fiscalDataService.getReceivers(companyId);
      setLista(data?.receivers || []);
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudieron cargar los clientes');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const abrir = (registro) => {
    setEditando(registro || {});
    form.setFieldsValue(registro || { use_tax: 'G03' });
  };

  const guardar = async (values) => {
    setGuardando(true);
    try {
      if (editando?.id) {
        await fiscalDataService.updateReceiver(companyId, editando.id, values);
        message.success('Cliente actualizado');
      } else {
        await fiscalDataService.createReceiver(companyId, values);
        message.success('Cliente registrado');
      }
      setEditando(null);
      form.resetFields();
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (registro) => {
    try {
      await fiscalDataService.deleteReceiver(companyId, registro.id);
      message.success('Cliente eliminado');
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const columns = [
    { title: 'RFC', dataIndex: 'tax_id', key: 'tax_id' },
    { title: 'Razón social', dataIndex: 'tax_name', key: 'tax_name' },
    { title: 'CP', dataIndex: 'tax_location', key: 'tax_location' },
    { title: 'Uso CFDI', dataIndex: 'use_tax', key: 'use_tax' },
    {
      title: '',
      key: 'acciones',
      render: (_, fila) => (
        <Space wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => abrir(fila)}>
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este cliente?"
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => eliminar(fila)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => abrir(null)}
        block={esMovil}
        size={esMovil ? 'large' : 'middle'}
        style={{ marginBottom: 12 }}
      >
        Registrar cliente
      </Button>

      <ResponsiveTable
        rowKey="id"
        loading={cargando}
        dataSource={lista}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        locale={{
          emptyText: <Empty description="Sin clientes registrados" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
      />

      <Modal
        title={editando?.id ? `Editar cliente — ${editando.tax_id}` : 'Registrar cliente'}
        open={editando !== null}
        onCancel={() => setEditando(null)}
        onOk={() => form.submit()}
        confirmLoading={guardando}
        okText="Guardar"
        cancelText="Cancelar"
        width={esMovil ? '100%' : 520}
        style={esMovil ? { top: 0, maxWidth: '100vw', margin: 0 } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item
            name="tax_id"
            label="RFC"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input maxLength={13} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item name="tax_name" label="Razón social" rules={[{ required: true, message: 'Requerido' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tax_location" label="Código postal" rules={[{ required: true, message: 'Requerido' }]}>
            <Input maxLength={5} />
          </Form.Item>
          <Form.Item name="use_tax" label="Uso del CFDI" rules={[{ required: true, message: 'Requerido' }]}>
            <Select options={USOS} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="fiscal_regimen" label="Régimen fiscal">
            <Input maxLength={3} />
          </Form.Item>
          <Form.Item name="email" label="Correo">
            <Input type="email" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ClientesPanel;
